import CoreUtils from './CoreUtils';
import DateTimeUtils from './DateTimeUtils';
import NumberUtils from './NumberUtils';

const millisecondsInSeconds = 1000;
const secondsInMinutes = 60;
const millisecondsInMinutes = millisecondsInSeconds * secondsInMinutes;
const minutesInHours = 60;
const millisecondsInHours = millisecondsInMinutes * minutesInHours;

export default class TimeUtils {
  static isValidTime(value) {
    return !CoreUtils.isDefinedAndNotNull(TimeUtils.parseTime(value));
  }

  static getNow(includeSeconds, includeMilliseconds) {
    var localNow = new Date();

    return TimeUtils.formatTime(
      localNow.getHours(),
      localNow.getMinutes(),
      includeSeconds
        ? localNow.getSeconds()
        : undefined,
      includeMilliseconds
        ? localNow.getMilliseconds()
        : undefined
    );
  }

  static compareTimes(time1, time2) {
    var time1Values = TimeUtils.parseTime(time1);
    var time2Values = TimeUtils.parseTime(time2);

    if (time1Values === null && time2Values === null)
      return 0;

    if (time2Values === null)
      return 1;

    if (time1Values === null)
      return -1;

    var time1Ticks = TimeUtils.convertToTicks(time1Values);
    var time2Ticks = TimeUtils.convertToTicks(time2Values);

    if (time1Ticks > time2Ticks)
      return 1;

    if (time1Ticks < time2Ticks)
      return -1;

    return 0;
  }

  static addTimeOffset(dateTime, timeOffset) {
    if (CoreUtils.isUndefinedOrNull(dateTime))
      return null;

    var isStringDate = false;
    if (typeof dateTime === 'string') {
      dateTime = new Date(dateTime);
      isStringDate = true;
    }
    else if (!(dateTime instanceof Date))
      return null;

    if (typeof timeOffset === 'string')
      timeOffset = this.parseTime(timeOffset);

    if (timeOffset === null)
      return null;

    var newOffset = dateTime.valueOf() + TimeUtils.convertToTicks(timeOffset);
    dateTime = new Date(newOffset);

    if (isStringDate)
      dateTime = DateTimeUtils.toDtoDateTime(dateTime);

    return dateTime;
  }

  static addSecondsOffset(dateTime, timeOffset) {
    if (CoreUtils.isUndefinedOrNull(dateTime))
      return null;

    var isStringDate = false;
    if (typeof dateTime === 'string') {
      dateTime = new Date(dateTime);
      isStringDate = true;
    }
    else if (!(dateTime instanceof Date))
      return null;

    if (typeof timeOffset !== 'number')
      return null;

    var newOffset = dateTime.valueOf() + timeOffset * millisecondsInSeconds;
    dateTime = new Date(newOffset);

    if (isStringDate)
      dateTime = DateTimeUtils.toDtoDateTime(dateTime);

    return dateTime;
  }

  static filterTime(time, includeSeconds, includeMilliseconds) {
    var timeValues = TimeUtils.parseTime(time);
    if (timeValues === null)
      return time;

    return TimeUtils.formatTime(
      timeValues.hours,
      timeValues.minutes,
      includeSeconds
        ? timeValues.seconds
        : undefined,
      includeMilliseconds
        ? timeValues.milliseconds
        : undefined
    );
  }

  static convertToTicks(time) {
    if (CoreUtils.isUndefinedOrNull(time))
      return null;

    return time.milliseconds
      + time.seconds * millisecondsInSeconds
      + time.minutes * secondsInMinutes * millisecondsInSeconds
      + time.hours * minutesInHours * secondsInMinutes * millisecondsInSeconds
  }

  static convertFromTicks(ticks) {
    if (CoreUtils.isUndefinedOrNull(ticks))
      return null;

    var truncMethod = ticks >= 0
      ? Math.floor
      : Math.ceil;

    return {
      hours: truncMethod(ticks / millisecondsInHours),
      minutes: truncMethod((ticks / millisecondsInMinutes) % minutesInHours),
      seconds: truncMethod((ticks / millisecondsInSeconds) % secondsInMinutes),
      milliseconds: ticks % millisecondsInSeconds,
    };
  }

  static formatShortTime(time, locale) {
    var timeValues = TimeUtils.parseTime(time);
    if (timeValues === null)
      return '';

    var dateTime = new Date(2000, 0, 1,
      timeValues.hours, timeValues.minutes, timeValues.seconds, timeValues.milliseconds);

    return DateTimeUtils.formatShortTime(dateTime, locale);
  }

  static formatTime(hours, minutes, seconds, milliseconds) {
    var formattedValue = `${NumberUtils.padZerosIfNeeded(hours, 2)}`;

    if (CoreUtils.isDefinedAndNotNull(minutes))
      formattedValue += `:${NumberUtils.padZerosIfNeeded(minutes, 2)}`;

    if (CoreUtils.isDefinedAndNotNull(seconds))
      formattedValue += `:${NumberUtils.padZerosIfNeeded(seconds, 2)}`;

    if (CoreUtils.isDefinedAndNotNull(milliseconds))
      formattedValue += `.${NumberUtils.padZerosIfNeeded(milliseconds, 3)}`;

    return formattedValue;
  }

  static createPlaceholderTime(time) {
    return CoreUtils.isDefinedAndNotNull(time)
      ? new Date('2000-01-01T' + time)
      : time;
  }

  static parseTimeFromDate(date) {
    return CoreUtils.isDefinedAndNotNull(date) && date instanceof Date
      ? TimeUtils.formatTime(date.getHours(), date.getMinutes(), date.getSeconds())
      : date;
  }

  static parseTime(time) {
    if (CoreUtils.isUndefinedOrNull(time))
      return null;

    if (!(typeof time === 'string'))
      return null;

    var timeValues = {
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0
    };

    var negativeValue = false;
    var phase = 0;
    var buffer = '';

    for (var index = 0; index < time.length; index++) {
      var charCode = time.charCodeAt(index);

      if (phase === 0) {
        if (charCode === 0x002B) {
          phase = 1;
          continue;
        } else if (charCode === 0x002D) {
          negativeValue = true;
          phase = 1;
          continue;
        } else {
          phase = 1;
        }
      }

      if (TimeUtils.isDigitCharCode(charCode)) {
        buffer += String.fromCharCode(charCode);
      } else if (charCode === 0x003A) {
        if (phase === 1 || phase === 2) {
          TimeUtils.commitBuffer(buffer, phase, timeValues);
          phase += 1;
          buffer = '';
        } else {
          return null;
        }
      } else if (charCode === 0x002E) {
        if (phase === 3) {
          TimeUtils.commitBuffer(buffer, phase, timeValues);
          phase += 1;
          buffer = '';
        } else {
          return null;
        }
      }
    }

    if (buffer.length > 0)
      TimeUtils.commitBuffer(buffer, phase, timeValues);

    if (timeValues.hours === null || timeValues.minutes === null
      || timeValues.seconds === null|| timeValues.milliseconds === null)
      return null;

    if (timeValues.minutes > 59 || timeValues.seconds > 59
      || timeValues.milliseconds > 999)
      return null;

    if (negativeValue) {
      timeValues.hours *= -1;
      timeValues.minutes *= -1;
      timeValues.seconds *= -1;
      timeValues.milliseconds *= -1;
    }

    return timeValues;
  }

  static commitBuffer(buffer, phase, timeValues) {
    var value = buffer.length > 0
      ? Number.parseInt(buffer)
      : null;

    switch (phase) {
      case 1:
        timeValues.hours = value;
        break;
      case 2:
        timeValues.minutes = value;
        break;
      case 3:
        timeValues.seconds = value;
        break;
      case 4:
        timeValues.milliseconds = value;
        break;
      default:
        break;
    }
  }

  static isDigitCharCode(charCode) {
    return charCode >= 0x0030 && charCode <= 0x0039;
  }
}